import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { ExternalLink } from 'lucide-react'
import { useInvestorContext } from '../../../../contexts/investor-guard'
import { useCopyToClipboard } from '../../../../hooks/use-copy-to-clipboard'
import { trackEvent } from '../../../../lib/gtag'
import { openIntercomWithMessage } from '../../../../lib/intercom'
import { useNotifyWhenInChatgpt } from '../../hooks/use-investor-assistant'
import type { AssistantGuide } from './assistant-guides'
import { StepText } from './step-text'

export const SERVER_URL = `${import.meta.env.VITE_BACKEND_URL}/mcp`

const adminRequest = `Please add SeedForge to our ChatGPT workspace as a custom MCP server: ${SERVER_URL}. Each of us then connects with our own SeedForge account.`

export function AdminRequestButton() {
  const { copied, copy } = useCopyToClipboard()
  return (
    <CtaButton
      size="sm"
      onClick={() => {
        copy(adminRequest)
        trackEvent('assistant_admin_request_copied')
      }}
    >
      {copied ? 'Copied' : 'Copy request for my admin'}
    </CtaButton>
  )
}

/** Opens support with the assistant and the firm filled in, so we can send the IT admin its set-up details. */
function useAskForSetupDetails(guide: AssistantGuide) {
  const { investor } = useInvestorContext()
  return () => {
    trackEvent('assistant_setup_details_requested', { assistant: guide.id })
    const target = guide.id === 'gemini' ? 'Gemini Enterprise' : guide.label
    openIntercomWithMessage(
      `Please send my IT admin the SeedForge set-up details for ${target}. Firm: ${investor.name}.`
    )
  }
}

function SetupDetailsButton({ guide }: { guide: AssistantGuide }) {
  const ask = useAskForSetupDetails(guide)
  return (
    <CtaButton size="sm" onClick={ask}>
      Ask us for set-up details
    </CtaButton>
  )
}

function FooterAction({ guide, chatgptNotify }: { guide: AssistantGuide; chatgptNotify: boolean }) {
  const ask = useAskForSetupDetails(guide)
  const notify = useNotifyWhenInChatgpt()

  if (guide.footerAction === 'ask-us') {
    return (
      <SecondaryButton size="sm" onClick={ask}>
        Ask us
      </SecondaryButton>
    )
  }
  if (guide.footerAction === 'tell-me-when') {
    return chatgptNotify ? (
      <span className="text-xs text-foreground">
        We will email you when SeedForge is in ChatGPT.
      </span>
    ) : (
      <SecondaryButton
        size="sm"
        disabled={notify.isPending}
        onClick={() => {
          trackEvent('assistant_chatgpt_notify')
          notify.mutate({})
        }}
      >
        Tell me when they can
      </SecondaryButton>
    )
  }
  return null
}

export function AssistantGuideCard({
  guide,
  chatgptNotify,
  onOpened,
}: {
  guide: AssistantGuide
  chatgptNotify: boolean
  /** The investor went to their assistant: start waiting for the sign-in. */
  onOpened: () => void
}) {
  return (
    <div className="rounded-xl border border-border">
      <div className="flex flex-wrap items-start justify-between gap-3 p-5">
        <p className="min-w-0 flex-1 text-sm font-medium text-foreground">{guide.intro}</p>
        {guide.adminRequest && <AdminRequestButton />}
        {guide.setupDetails && <SetupDetailsButton guide={guide} />}
        {guide.open && (
          <SecondaryButton
            size="sm"
            icon={<ExternalLink className="size-3.5" />}
            onClick={() => {
              trackEvent('assistant_open_clicked', { assistant: guide.id })
              window.open(guide.open?.href, '_blank', 'noopener,noreferrer')
              onOpened()
            }}
          >
            {guide.open.label}
          </SecondaryButton>
        )}
      </div>
      <ol className="space-y-3 px-5 pb-5">
        {guide.steps.map((step, i) => (
          <li key={step} className="flex items-start gap-3 text-sm text-foreground">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
              {i + 1}
            </span>
            <span className="pt-0.5 leading-relaxed">
              <StepText text={step} />
            </span>
          </li>
        ))}
      </ol>
      {guide.footer && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-3">
          <p className="text-xs text-muted-foreground">{guide.footer}</p>
          <FooterAction guide={guide} chatgptNotify={chatgptNotify} />
        </div>
      )}
    </div>
  )
}
